import React, { useEffect, useMemo, useState } from 'react';
import CryptoJS from 'crypto-js';
import { useTranslation } from 'react-i18next';
import api from '../api';
import './estilodashuser.css';

const gerarChavePadrao = (conta) =>
  CryptoJS.MD5(String(Number(conta) * 11 - 181818)).toString(
    CryptoJS.enc.Hex
  );

const GERADORES = {
  ea: gerarChavePadrao,
  bias: gerarChavePadrao,
  ragnarok: gerarChavePadrao,
};

function getStatusValue(value) {
  return Number(value) === 1 ? 1 : 0;
}

function normalizeProdutoKey(value) {
  const s = String(value || '').trim().toLowerCase();

  if (
    s === 'ea' ||
    s.includes('quantum trades ea') ||
    s === 'quantum trades' ||
    s.includes('quantumtrades ea')
  ) {
    return 'ea';
  }

  if (s === 'bias' || s.includes('quantumtrades bias')) {
    return 'bias';
  }

  if (s === 'ragnarok' || s.includes('quantumtrades ragnarok')) {
    return 'ragnarok';
  }

  return s;
}

function getTipoAssinatura(assinatura) {
  const t = String(assinatura?.tipo || '').toLowerCase();
  return t === 'pacote' ? 'pacote' : 'individual';
}

export default function AdicionarLicenca({ userId }) {
  const { t, i18n } = useTranslation();
  const [assinaturas, setAssinaturas] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [assinaturaId, setAssinaturaId] = useState('');
  const [produtoId, setProdutoId] = useState('');
  const [produtoCodigo, setProdutoCodigo] = useState('');

  const [conta, setConta] = useState('');
  const [chave, setChave] = useState('');
  const [tipo, setTipo] = useState('Demo');

  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (userId) {
      carregarDados();
    }
  }, [userId]);

  const carregarDados = async ({ manterMensagem = false } = {}) => {
    try {
      setCarregando(true);
      if (!manterMensagem) {
        setMensagem('');
        setTipoMensagem('');
      }

      const [respAss, respLic, respProd, respPac] = await Promise.all([
        api.get('/api/minhas-assinaturas'),
        api.get('/api/minhas-licencas'),
        api.get('/api/produtos-ativos'),
        api.get('/api/pacotes-ativos'),
      ]);

      setAssinaturas(respAss.data || []);
      setLicencas(respLic.data || []);
      setProdutos(respProd.data || []);
      setPacotes(respPac.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados para adicionar licença:', error);
      setMensagem(t('dashboard.addLicense.loadError'));
      setTipoMensagem('erro');
    } finally {
      setCarregando(false);
    }
  };

  const assinaturasAtivas = useMemo(() => {
    return assinaturas.filter((a) => getStatusValue(a.status) === 1);
  }, [assinaturas]);

  const assinaturaSelecionada = useMemo(() => {
    return (
      assinaturasAtivas.find((a) => String(a.id) === String(assinaturaId)) ||
      null
    );
  }, [assinaturasAtivas, assinaturaId]);

  const produtosPermitidos = useMemo(() => {
    if (!assinaturaSelecionada) return [];

    const tipoAss = getTipoAssinatura(assinaturaSelecionada);
    const codigoPlano = String(assinaturaSelecionada.codigo_plano || '').trim();

    if (tipoAss === 'individual') {
      const codigoNormalizado = normalizeProdutoKey(codigoPlano);

      const produto = produtos.find(
        (p) =>
          normalizeProdutoKey(p.codigo) === codigoNormalizado ||
          normalizeProdutoKey(p.nome) === codigoNormalizado
      );

      if (!produto) return [];

      return [
        {
          id: Number(produto.id),
          codigo: produto.codigo,
          nome: produto.nome,
        },
      ];
    }

    const pacote = pacotes.find(
      (p) =>
        String(p.codigo || '').trim().toLowerCase() ===
        codigoPlano.toLowerCase()
    );

    if (!pacote || !Array.isArray(pacote.produtos)) return [];

    return pacote.produtos.map((p) => ({
      id: Number(p.id),
      codigo: p.codigo,
      nome: p.nome,
    }));
  }, [assinaturaSelecionada, produtos, pacotes]);

  useEffect(() => {
    if (!assinaturaSelecionada) {
      setProdutoId('');
      setProdutoCodigo('');
      return;
    }

    if (produtosPermitidos.length === 1) {
      const unico = produtosPermitidos[0];
      setProdutoId(String(unico.id));
      setProdutoCodigo(unico.codigo || '');
      return;
    }

    if (
      produtoId &&
      !produtosPermitidos.some((p) => String(p.id) === String(produtoId))
    ) {
      setProdutoId('');
      setProdutoCodigo('');
    }
  }, [assinaturaSelecionada, produtosPermitidos, produtoId]);

  const resumoProduto = useMemo(() => {
    if (!assinaturaSelecionada || !produtoId) {
      return {
        slotsContratados: 0,
        licencasAtivasProduto: 0,
        slotsDisponiveis: 0,
      };
    }

    const slotsContratados = Number(assinaturaSelecionada.slots_contratados || 0);

    const licencasAtivasProduto = licencas.filter(
      (l) =>
        getStatusValue(l.status) === 1 &&
        normalizeProdutoKey(l.produto_codigo || l.produto_nome) ===
          normalizeProdutoKey(produtoCodigo || '')
    ).length;

    const slotsDisponiveis = Math.max(
      slotsContratados - licencasAtivasProduto,
      0
    );

    return {
      slotsContratados,
      licencasAtivasProduto,
      slotsDisponiveis,
    };
  }, [assinaturaSelecionada, licencas, produtoId, produtoCodigo]);

  const resumoGeral = useMemo(() => {
    const licencasAtivas = licencas.filter((l) => getStatusValue(l.status) === 1);

    return {
      totalAssinaturasAtivas: assinaturasAtivas.length,
      totalLicencas: licencas.length,
      totalLicencasAtivas: licencasAtivas.length,
    };
  }, [assinaturasAtivas, licencas]);

  const handleTrocarAssinatura = (value) => {
    setAssinaturaId(value);
    setProdutoId('');
    setProdutoCodigo('');
    setConta('');
    setChave('');
    setMensagem('');
    setTipoMensagem('');
  };

  const handleTrocarProduto = (value) => {
    const produto = produtosPermitidos.find(
      (p) => String(p.id) === String(value)
    );

    setProdutoId(value);
    setProdutoCodigo(produto?.codigo || '');
    setChave('');
    setMensagem('');
    setTipoMensagem('');
  };

  const handleGerarChave = () => {
    if (!produtoCodigo) {
      setMensagem(t('dashboard.addLicense.selectProductFirst'));
      setTipoMensagem('erro');
      return;
    }

    if (!conta) {
      setMensagem(t('dashboard.addLicense.enterAccount'));
      setTipoMensagem('erro');
      return;
    }

    const contaNum = Number(conta);
    if (Number.isNaN(contaNum)) {
      setMensagem(t('dashboard.addLicense.invalidAccount'));
      setTipoMensagem('erro');
      return;
    }

    const produtoKey = normalizeProdutoKey(produtoCodigo);
    const fn = GERADORES[produtoKey] || GERADORES.ea;

    setChave(fn(conta));
    setMensagem('');
    setTipoMensagem('');
  };

  const handleSalvarLicenca = async () => {
    if (salvando) return;

    if (!assinaturaId) {
      setMensagem(t('dashboard.addLicense.selectSubscriptionError'));
      setTipoMensagem('erro');
      return;
    }

    if (!produtoId) {
      setMensagem(t('dashboard.addLicense.selectProductError'));
      setTipoMensagem('erro');
      return;
    }

    if (!conta) {
      setMensagem(t('dashboard.addLicense.enterAccount'));
      setTipoMensagem('erro');
      return;
    }

    if (!chave) {
      setMensagem(t('dashboard.addLicense.generateFirst'));
      setTipoMensagem('erro');
      return;
    }

    if (resumoProduto.slotsDisponiveis <= 0) {
      setMensagem(
        t('dashboard.addLicense.noSlots')
      );
      setTipoMensagem('erro');
      return;
    }

    const contaDuplicadaMesmoProduto = licencas.find(
      (l) =>
        String(l.conta || '') === String(conta) &&
        normalizeProdutoKey(l.produto_codigo || l.produto_nome) ===
          normalizeProdutoKey(produtoCodigo)
    );

    if (contaDuplicadaMesmoProduto) {
      setMensagem(t('dashboard.addLicense.duplicate'));
      setTipoMensagem('erro');
      return;
    }

    try {
      setSalvando(true);

      await api.post('/api/minhas-licencas', {
        assinatura_id: Number(assinaturaId),
        produto_id: Number(produtoId),
        conta: String(conta).trim(),
        lkey: String(chave).trim(),
        tipo,
      });

      setMensagem(
        t('dashboard.addLicense.success')
      );
      setTipoMensagem('sucesso');

      setConta('');
      setChave('');
      setTipo('Demo');

      await carregarDados({ manterMensagem: true });
    } catch (error) {
      console.error('Erro ao salvar licença:', error);
      setMensagem(
        i18n.resolvedLanguage === 'en'
          ? t('dashboard.addLicense.saveError')
          : error.response?.data?.message || t('dashboard.addLicense.saveError')
      );
      setTipoMensagem('erro');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="adicionar-licenca-container">
      <center>
        <h3>{t('dashboard.addLicense.title')}</h3>
        <p>{t('dashboard.addLicense.intro')}</p>
        <p>{t('dashboard.addLicense.slotsInfo')}</p>
      </center>

      <div className="lic-kpis lic-kpis-compact">
        <div className="lic-kpi">
          <div className="lic-kpi-label">{t('dashboard.addLicense.activeSubscriptions')}</div>
          <div className="lic-kpi-value">{resumoGeral.totalAssinaturasAtivas}</div>
        </div>
        <div className="lic-kpi">
          <div className="lic-kpi-label">{t('dashboard.addLicense.registeredLicenses')}</div>
          <div className="lic-kpi-value">{resumoGeral.totalLicencas}</div>
        </div>
        <div className="lic-kpi">
          <div className="lic-kpi-label">{t('dashboard.addLicense.activeLicenses')}</div>
          <div className="lic-kpi-value">{resumoGeral.totalLicencasAtivas}</div>
        </div>
        <div className="lic-kpi">
          <div className="lic-kpi-label">{t('dashboard.addLicense.availableProduct')}</div>
          <div className="lic-kpi-value">
            {produtoId ? resumoProduto.slotsDisponiveis : '—'}
          </div>
        </div>
      </div>

      {mensagem && (
        <div
          role={tipoMensagem === 'erro' ? 'alert' : 'status'}
          aria-live="polite"
          className={
            tipoMensagem === 'erro'
              ? 'alert-box alert-error'
              : 'alert-box alert-success'
          }
        >
          {mensagem}
        </div>
      )}

      {carregando && <p className="lic-loading">{t('dashboard.addLicense.loading')}</p>}

      <form className="adicionar-licenca-form" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor="assinatura">{t('dashboard.addLicense.subscription')}</label>
        <select
          id="assinatura"
          value={assinaturaId}
          onChange={(e) => handleTrocarAssinatura(e.target.value)}
          disabled={salvando}
        >
          <option value="">{t('dashboard.addLicense.selectSubscription')}</option>
          {assinaturasAtivas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nome_plano || a.codigo_plano} ({a.tier}) -{' '}
              {getTipoAssinatura(a) === 'pacote' ? t('dashboard.addLicense.package') : t('dashboard.addLicense.individual')}
            </option>
          ))}
        </select>

        <label htmlFor="produto">{t('dashboard.addLicense.product')}</label>
        <select
          id="produto"
          value={produtoId}
          onChange={(e) => handleTrocarProduto(e.target.value)}
          disabled={!assinaturaId || salvando}
        >
          <option value="">{t('dashboard.addLicense.selectProduct')}</option>
          {produtosPermitidos.map((produto) => (
            <option key={produto.id} value={produto.id}>
              {produto.nome} ({produto.codigo})
            </option>
          ))}
        </select>

        {assinaturaSelecionada && produtoId && (
          <div className="subscription-mini-box">
            <div className="subscription-mini-title">{t('dashboard.addLicense.summary')}</div>
            <div>{t('dashboard.addLicense.subscription')}: <strong>{assinaturaSelecionada.nome_plano || assinaturaSelecionada.codigo_plano}</strong></div>
            <div>{t('dashboard.addLicense.tier')}: <strong>{assinaturaSelecionada.tier}</strong></div>
            <div>{t('dashboard.addLicense.productLabel')}: <strong>{produtosPermitidos.find((p) => String(p.id) === String(produtoId))?.nome || '—'}</strong></div>
            <div>
              {t('dashboard.addLicense.productSlots')}: <strong>{resumoProduto.slotsContratados}</strong> •{' '}
              {t('dashboard.addLicense.inUse')}: <strong>{resumoProduto.licencasAtivasProduto}</strong> •{' '}
              {t('dashboard.addLicense.available')}: <strong>{resumoProduto.slotsDisponiveis}</strong>
            </div>
          </div>
        )}

        <label htmlFor="conta">{t('dashboard.addLicense.account')}</label>
        <input
          type="text"
          id="conta"
          value={conta}
          onChange={(e) => setConta(e.target.value)}
          placeholder={t('dashboard.addLicense.accountPlaceholder')}
          disabled={salvando}
        />

        <button
          type="button"
          onClick={handleGerarChave}
          className="botao-gerar-chave qt-btn qt-btn-secondary"
          disabled={!produtoId || salvando}
        >
          {t('dashboard.addLicense.generateKey')}
        </button>

        <label htmlFor="chave">{t('dashboard.addLicense.key')}</label>
        <input
          type="text"
          id="chave"
          value={chave}
          readOnly
        />

        <label htmlFor="tipo">{t('dashboard.addLicense.licenseType')}</label>
        <select
          id="tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="select-tipo"
          disabled={salvando}
        >
          <option value="Real">Real</option>
          <option value="Demo">Demo</option>
        </select>

        <button
          type="button"
          onClick={handleSalvarLicenca}
          className="botao-salvar-licenca qt-btn qt-btn-primary"
          disabled={!produtoId || resumoProduto.slotsDisponiveis <= 0 || salvando}
        >
          {salvando ? t('dashboard.addLicense.saving') : t('dashboard.addLicense.save')}
        </button>
      </form>

      {licencas.length > 0 && (
        <div className="licencas-existentes">
          <h4>{t('dashboard.addLicense.existing')}</h4>
          <table>
            <thead>
              <tr>
                <th>{t('dashboard.addLicense.productLabel')}</th>
                <th>{t('dashboard.licenses.account')}</th>
                <th>{t('dashboard.licenses.type')}</th>
                <th>{t('dashboard.addLicense.status')}</th>
              </tr>
            </thead>
            <tbody>
              {licencas.map((licenca) => (
                <tr key={licenca.id}>
                  <td>{licenca.produto_nome || licenca.produto_codigo || t('dashboard.addLicense.productFallback')}</td>
                  <td>{licenca.conta}</td>
                  <td>{licenca.tipo || '—'}</td>
                  <td>
                    {getStatusValue(licenca.status) === 1 ? t('common.active') : t('common.inactive')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
